/**
 * DebugSessionOverlay — the floating HUD for a free-roam Debug Session. Rendered
 * by DevModeIndicator (already mounted, non-frozen) and bound to the
 * debugSessionController singleton, so it floats over the real app across
 * navigation without a provider mount.
 *
 * Evidence model: every capture (fast shot, true-pixel shot, inspected element,
 * pin markup) APPENDS to a staged evidence tray — a visible thumbnail queue —
 * and "Save finding" stores the whole tray on one finding (multiple screenshots
 * per finding). Nothing replaces silently; every capture confirms with a toast
 * and a Dev Console event; closing with staged evidence asks first.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Paper, Typography, IconButton, Button, TextField, MenuItem, Chip, Stack, Tooltip, CircularProgress, alpha,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import PushPinIcon from '@mui/icons-material/PushPin';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { debugService } from '../../services/debugService';
import { debugSessionApi, type FindingType } from '../../services/debugSessionApi';
import { debugSessionController, useDebugSession } from '../../services/debugSessionController';
import { useToast } from '../../contexts/ToastContext';
import DebugInspector, { type InspectorResult, type Pin } from './DebugInspector';
import ImageLightbox from './ImageLightbox';
import type { CapturedElement } from './debugInspectUtils';

const TYPE_META: Record<FindingType, { label: string; emoji: string; color: 'error' | 'secondary' | 'info' | 'default' }> = {
  bug: { label: 'Bug', emoji: '🐞', color: 'error' },
  design: { label: 'Design', emoji: '🎨', color: 'secondary' },
  feature: { label: 'Feature', emoji: '💡', color: 'info' },
  observation: { label: 'Note', emoji: '📝', color: 'default' },
};

const MAX_SHOTS = 10;

// Z-ladder (owner-reported: the HUD hid behind the z-1500 composer, and its
// Select menus opened invisibly BEHIND it at MUI-default 1300, backdrop-locking
// the app). App overlays top out at ~10000 (command-view poppers / the dev-mode
// tap target); the picker owns 2147483000 (tied only by the capacity design
// modals — known limitation). Toasts sit above the HUD at 20200 (Entry 235).
const HUD_Z = 20000;
const HUD_MENU_Z = 20100;

const POS_KEY = 'bsg_debug_hud_pos';

type ShotKind = 'html2canvas' | 'true_pixel' | 'element' | 'annotated' | 'pasted';
interface Shot { id: number; blob: Blob; url: string; kind: ShotKind }
const KIND_LABEL: Record<ShotKind, string> = {
  html2canvas: 'fast', true_pixel: 'true-pixel', element: 'element', annotated: 'pin markup', pasted: 'pasted',
};

let shotSeq = 0;

const DebugSessionOverlay: React.FC = () => {
  const s = useDebugSession();
  const toast = useToast();
  const [minimized, setMinimized] = useState(false);
  const [note, setNote] = useState('');
  const [type, setType] = useState<FindingType>('bug');
  const [severity, setSeverity] = useState('normal');
  const [shooting, setShooting] = useState(false);
  const [inspectorMode, setInspectorMode] = useState<'inspect' | 'pin' | null>(null);
  const [sharingTick, setSharingTick] = useState(0); // re-render after stream start/stop

  // staged evidence for the next Save — an appending tray, never a silent
  // replace. The ref is the synchronous authority (pin-done stages two shots
  // back-to-back in one tick); state mirrors it for render.
  const [shots, setShots] = useState<Shot[]>([]);
  const shotsRef = useRef<Shot[]>([]);
  const [element, setElement] = useState<CapturedElement | null>(null);
  const [pins, setPins] = useState<Pin[] | null>(null);
  const hasStagedRef = useRef(false); // current staged state for the finish/close guards
  const [lightbox, setLightbox] = useState<string | null>(null);

  // Draggable HUD (owner: "the overlay blocks a part of the app because I
  // can't move it around") — SmokeRunHUD's pattern: header mousedown + window
  // listeners; position persisted and clamped so a saved spot from a bigger
  // monitor can't strand it off-screen. null = default bottom-right anchor.
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) return null;
      return { x: Math.min(Math.max(0, p.x), window.innerWidth - 80), y: Math.min(Math.max(0, p.y), window.innerHeight - 48) };
    } catch { return null; }
  });
  const onDragStart = useCallback((e: React.MouseEvent) => {
    const rect = paperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const onMove = (ev: MouseEvent) => {
      const x = Math.min(Math.max(0, ev.clientX - dx), window.innerWidth - Math.min(rect.width, 120));
      const y = Math.min(Math.max(0, ev.clientY - dy), window.innerHeight - 40);
      setPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setPos((cur) => { try { if (cur) localStorage.setItem(POS_KEY, JSON.stringify(cur)); } catch { /* noop */ } return cur; });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // D4 — the note textarea couldn't be typed into while a MUI Dialog was open:
  // the Dialog's focus trap (enforceFocus) yanks focus back whenever it leaves
  // the dialog subtree, and this HUD portals to <body> outside it. Intercept
  // focusin at capture phase for our own subtree and stopImmediatePropagation
  // so MUI's document-level focus guard never runs for it. Active only while a
  // session is live.
  useEffect(() => {
    if (!s.active) return undefined;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Element | null;
      if (t && (t as Element).closest && (t as Element).closest('[data-debug-overlay], [data-debug-inspector]')) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, [s.active]);

  // F1 — error sentinel (groundwork for the autonomous QA agent): while the
  // session is live, watch the dev-mode buffer for NEW error-level events and
  // badge them in the HUD with a one-click attach. Nothing auto-saves.
  const [errWatch, setErrWatch] = useState<{ count: number; latest: string[] }>({ count: 0, latest: [] });
  const seenErrIdRef = useRef(0);
  const summarizeErr = (e: { type: string; data: Record<string, any> }): string => {
    if (e.type === 'api_error' || e.type === 'api_response') return `${e.data.method || ''} ${e.data.url || ''} ${e.data.status || ''} ${e.data.message || ''}`.trim().slice(0, 110);
    if (Array.isArray(e.data.args)) return e.data.args.join(' ').slice(0, 110);
    return String(e.data.message || e.type).slice(0, 110);
  };
  useEffect(() => {
    if (!s.active) return undefined;
    const sync = () => {
      const errs = debugService.getEvents().filter((e) => e.level === 'error' && e.id > seenErrIdRef.current);
      setErrWatch({ count: errs.length, latest: errs.slice(-3).map(summarizeErr) });
    };
    sync();
    return debugService.subscribe(sync);
  }, [s.active]);

  const attachErrors = useCallback(() => {
    const errs = debugService.getEvents().filter((e) => e.level === 'error' && e.id > seenErrIdRef.current);
    if (!errs.length) return;
    const lines = errs.slice(-6).map((e) => `- [${e.type}] ${summarizeErr(e)}`);
    setNote((n) => `${n ? `${n}\n\n` : ''}Errors observed:\n${lines.join('\n')}`);
    setType('bug');
    seenErrIdRef.current = errs[errs.length - 1].id;
    setErrWatch({ count: 0, latest: [] });
    setMinimized(false);
    debugService.logSessionAction('debug_session_errors_attached', { count: errs.length });
  }, []);

  const commitShots = useCallback((list: Shot[]) => { shotsRef.current = list; setShots(list); }, []);

  const clearStaged = useCallback(() => {
    shotsRef.current.forEach((sh) => URL.revokeObjectURL(sh.url));
    commitShots([]);
    setElement(null);
    setPins(null);
  }, [commitShots]);

  /** Append to the tray; returns the new tray size, or -1 when full. */
  const stageShot = useCallback((blob: Blob, kind: ShotKind): number => {
    const cur = shotsRef.current;
    if (cur.length >= MAX_SHOTS) return -1;
    const next = [...cur, { id: ++shotSeq, blob, url: URL.createObjectURL(blob), kind }];
    commitShots(next);
    setMinimized(false); // evidence landed — make sure it's visible
    return next.length;
  }, [commitShots]);

  const removeShot = useCallback((id: number) => {
    const sh = shotsRef.current.find((x) => x.id === id);
    if (sh) URL.revokeObjectURL(sh.url);
    commitShots(shotsRef.current.filter((x) => x.id !== id));
  }, [commitShots]);

  const afterShot = useCallback((count: number, kind: ShotKind) => {
    if (count > 0) {
      debugService.logSessionAction('debug_session_shot_staged', { kind, staged: count });
      toast.showToast(`${KIND_LABEL[kind]} screenshot staged (${count}/${MAX_SHOTS}) — Save finding stores it`, 'info');
    } else {
      toast.showToast(`Tray is full (${MAX_SHOTS} shots) — save the finding or remove a shot first`, 'warning');
    }
  }, [toast]);

  const captureHtml = useCallback(async () => {
    setShooting(true);
    try {
      const b = await debugService.captureCanvasBlob();
      if (b) afterShot(stageShot(b, 'html2canvas'), 'html2canvas');
      else toast.showToast('Fast shot couldn’t render this page — use True (screen capture) or paste/attach your own image (⌘V in the note)', 'warning');
    } finally { setShooting(false); }
  }, [stageShot, afterShot, toast]);

  // #006 — the universal escape hatch: paste (⌘V) or attach an image so evidence
  // survives even when html2canvas can't render a page (the calendar case). The
  // image stages as a normal tray shot via the existing multi-shot upload path.
  const stageImageFile = useCallback((file: File | null | undefined): boolean => {
    if (!file || !file.type.startsWith('image/')) return false;
    afterShot(stageShot(file, 'pasted'), 'pasted');
    return true;
  }, [stageShot, afterShot]);

  const onNotePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const f = items[i].getAsFile();
        if (f && stageImageFile(f)) { e.preventDefault(); return; } // image → tray, don't paste bytes as text
      }
    }
  }, [stageImageFile]);

  const onAttachChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    let staged = 0;
    for (const f of files) if (stageImageFile(f)) staged++;
    if (!staged && files.length) toast.showToast('Only image files can be attached', 'warning');
    e.target.value = ''; // allow re-selecting the same file
  }, [stageImageFile, toast]);

  const captureTrue = useCallback(async () => {
    setShooting(true);
    debugSessionController.setCapturing(true); // hide overlay so it's not in the shot
    try {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 60)));
      const b = await debugService.captureTruePixelBlob();
      if (b) afterShot(stageShot(b, 'true_pixel'), 'true_pixel');
      else toast.showToast('True-pixel capture unavailable (share was declined or the browser blocked it)', 'warning');
    } finally {
      debugSessionController.setCapturing(false);
      setShooting(false);
      setSharingTick((t) => t + 1);
    }
  }, [stageShot, afterShot, toast]);

  const stopSharing = useCallback(() => {
    debugService.stopDisplayStream();
    setSharingTick((t) => t + 1);
    toast.showToast('Screen sharing stopped — the next True capture will ask again', 'info');
  }, [toast]);

  const onInspectorDone = useCallback((r: InspectorResult) => {
    setInspectorMode(null);
    setMinimized(false);
    if (r.element) {
      setElement(r.element);
      toast.showToast(`Element captured: ${r.element.selector.split('>').pop()?.trim() || r.element.tag} — staged on this finding`, 'info');
    }
    if (r.pins) {
      setPins(r.pins);
      if (r.pins.length) toast.showToast(`${r.pins.length} pin${r.pins.length > 1 ? 's' : ''} staged — Save finding stores them`, 'info');
    }
    const dropped: string[] = [];
    if (r.screenshot && stageShot(r.screenshot, r.element ? 'element' : 'html2canvas') < 0) dropped.push(r.element ? 'element shot' : 'page shot');
    if (r.annotated && stageShot(r.annotated, 'annotated') < 0) dropped.push('pin-markup shot');
    if (dropped.length) toast.showToast(`Tray is full (${MAX_SHOTS}) — ${dropped.join(' + ')} NOT staged. Save or remove a shot, then recapture.`, 'warning');
    if (r.error) toast.showToast(r.error, 'warning');
  }, [stageShot, toast]);

  const saveFinding = useCallback(async () => {
    if (!note.trim() && !window.confirm('Save this finding without a note? (A note is what makes it actionable later.)')) return;
    // Title = first line, cut at a word boundary — mid-word chops ("tabbed
    // bulle") read badly everywhere the title renders.
    const firstLine = note.trim().split('\n')[0];
    let title = firstLine.slice(0, 90);
    if (firstLine.length > 90) {
      const sp = title.lastIndexOf(' ');
      if (sp > 40) title = title.slice(0, sp);
      title += '…';
    }
    title = title || TYPE_META[type].label;
    const annotatedShot = shots.find((sh) => sh.kind === 'annotated') || null;
    const primaryShot = shots.find((sh) => sh !== annotatedShot) || null;
    const extras = shots.filter((sh) => sh !== annotatedShot && sh !== primaryShot);
    const finding = await debugSessionController.addFinding(
      {
        title, note: note.trim() || undefined, finding_type: type, severity,
        shot_type: primaryShot?.kind,
        element: element ?? undefined,
        source_ref: element?.source_ref ?? undefined,
        annotations: pins?.length ? { pins } : undefined,
        extra_meta: extras.map((sh) => ({ shot_type: sh.kind })),
      },
      { screenshot: primaryShot?.blob ?? null, annotated: annotatedShot?.blob ?? null, extra: extras.map((sh) => sh.blob) },
    );
    if (finding) {
      const n = shots.length;
      toast.showToast(`Finding #${finding.seq} saved${n ? ` with ${n} screenshot${n > 1 ? 's' : ''}` : ''}`, 'success');
      debugService.logSessionAction('debug_session_finding_saved', { seq: finding.seq, shots: n, type });
      // Errors that existed at save time rode this finding's trail/HAR —
      // don't re-badge them on the next one.
      const errs = debugService.getEvents().filter((e) => e.level === 'error');
      if (errs.length) seenErrIdRef.current = errs[errs.length - 1].id;
      setErrWatch({ count: 0, latest: [] });
      setNote('');
      clearStaged();
    }
  }, [note, type, severity, shots, element, pins, clearStaged, toast]);

  const finishAndExport = useCallback(async () => {
    if (hasStagedRef.current && !window.confirm('You have staged evidence that is NOT saved to a finding yet — finish anyway and lose it?')) return;
    const title = s.title || 'debug-session';
    const token = await debugSessionController.finish();
    setNote('');
    clearStaged(); // never leak this session's staged evidence into the next one
    if (token) { try { await debugSessionApi.downloadZip(token, `${title}.zip`); } catch { /* non-fatal */ } }
  }, [s.title, clearStaged]);

  const closeOverlay = useCallback(() => {
    if (hasStagedRef.current
      && !window.confirm('Close the debug session HUD? Staged (unsaved) evidence will be lost — saved findings stay on the server and the session resumes from the DEV banner or a reload.')) {
      return;
    }
    setNote('');
    clearStaged(); // the confirm said "lost", so actually drop it (and free the blob URLs)
    debugSessionController.cancel();
  }, [clearStaged]);

  if (!s.active) return null;
  const sharing = debugService.isDisplayStreamActive();
  void sharingTick;
  const hasStaged = !!(shots.length || element || pins?.length || note.trim());
  hasStagedRef.current = hasStaged;

  const body = (
    <Paper
      data-debug-overlay="true"
      elevation={8}
      ref={paperRef}
      sx={{
        position: 'fixed',
        ...(pos ? { left: pos.x, top: pos.y } : { right: 16, bottom: 16 }),
        zIndex: HUD_Z, width: minimized ? 260 : 356,
        borderRadius: 2, overflow: 'hidden', visibility: s.capturing ? 'hidden' : 'visible',
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Box
        onMouseDown={onDragStart}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: 'grey.900', color: 'common.white', cursor: 'grab', '&:active': { cursor: 'grabbing' }, userSelect: 'none' }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          🐞 Debug session — {s.title || 'untitled'}
        </Typography>
        <Chip size="small" label={`${s.findings.length}`} sx={{ height: 18, color: 'common.white', bgcolor: 'rgba(255,255,255,0.18)' }} />
        <Tooltip title="Minimize"><IconButton size="small" sx={{ color: 'common.white' }} onClick={() => setMinimized((m) => !m)}><RemoveIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Close the HUD (saved findings + session stay on the server; resumable)">
          <IconButton size="small" sx={{ color: 'common.white' }} onClick={closeOverlay}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {!minimized && (
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            <Tooltip title="Fast screenshot (html2canvas render — instant, no prompt)"><span><Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} disabled={shooting} onClick={captureHtml}>Shot</Button></span></Tooltip>
            <Tooltip title={'True-pixel capture — the browser will ask you to share your screen: pick THIS tab. It asks once per session; later True shots reuse the stream instantly.'}><span><Button size="small" variant="outlined" startIcon={<ScreenshotMonitorIcon />} disabled={shooting} onClick={captureTrue}>True</Button></span></Tooltip>
            <Tooltip title="Inspect an element (selector + HTML + styles + source). The app won't react while picking."><span><Button size="small" variant="outlined" startIcon={<CenterFocusStrongIcon />} onClick={() => setInspectorMode('inspect')}>Inspect</Button></span></Tooltip>
            <Tooltip title="Drop comment pins anywhere — click for a point, drag for a region (great for 'a button should be here')"><span><Button size="small" variant="outlined" startIcon={<PushPinIcon />} onClick={() => setInspectorMode('pin')}>Pin</Button></span></Tooltip>
            <Tooltip title="Attach your own image (or paste one with ⌘V into the note) — works even when a page won't screenshot"><span><Button size="small" variant="outlined" component="label" startIcon={<AttachFileIcon />}>Image<input type="file" accept="image/*" hidden multiple onChange={onAttachChange} /></Button></span></Tooltip>
            {shooting && <CircularProgress size={18} sx={{ alignSelf: 'center' }} />}
          </Stack>

          {sharing && (
            <Chip
              size="small" color="error" variant="outlined"
              icon={<ScreenshotMonitorIcon sx={{ fontSize: '14px !important' }} />}
              label="Screen sharing active (reused for True shots)"
              onDelete={stopSharing}
              deleteIcon={<Tooltip title="Stop sharing"><StopCircleIcon /></Tooltip>}
              sx={{ mb: 1, maxWidth: '100%' }}
            />
          )}

          {errWatch.count > 0 && (
            <Box sx={{ mb: 1, p: 0.75, border: '1px solid #ef4444', borderRadius: 1, bgcolor: alpha('#ef4444', 0.06) }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="caption" sx={{ flex: 1, color: '#ef4444', fontWeight: 700 }}>
                  ⚠ {errWatch.count} error{errWatch.count > 1 ? 's' : ''} since last capture
                </Typography>
                <Button size="small" color="error" variant="outlined" onClick={attachErrors} sx={{ py: 0, minWidth: 0, fontSize: 11, textTransform: 'none' }}>
                  Attach
                </Button>
              </Stack>
              {errWatch.latest.map((line, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          {shots.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Staged evidence ({shots.length}{shots.length >= MAX_SHOTS ? ' · full' : ''}) — attaches on Save finding
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5 }}>
                {shots.map((sh) => (
                  <Box key={sh.id} sx={{ position: 'relative', flex: '0 0 auto', width: 96, borderRadius: 1, overflow: 'hidden', border: (t) => `1px solid ${t.palette.divider}` }}>
                    <img
                      src={sh.url} alt={KIND_LABEL[sh.kind]}
                      onClick={() => setLightbox(sh.url)}
                      style={{ width: '100%', height: 60, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                    />
                    <Chip size="small" label={KIND_LABEL[sh.kind]} sx={{ position: 'absolute', top: 2, left: 2, height: 16, fontSize: 9 }} />
                    <IconButton size="small" onClick={() => removeShot(sh.id)} sx={{ position: 'absolute', top: 0, right: 0, p: 0.25, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {(element || pins?.length) && (
            <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
              {element && <Chip size="small" variant="outlined" label={`el: ${element.selector.split('>').pop()?.trim() || element.tag}`} onDelete={() => setElement(null)} sx={{ height: 20, maxWidth: '100%' }} />}
              {!!pins?.length && <Chip size="small" variant="outlined" color="secondary" label={`${pins.length} pin${pins.length > 1 ? 's' : ''}${pins.some((p) => p.w !== undefined) ? ' (incl. region)' : ''}`} onDelete={() => setPins(null)} sx={{ height: 20 }} />}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value as FindingType)} sx={{ flex: 1 }}
              SelectProps={{ MenuProps: { sx: { zIndex: HUD_MENU_Z } } }}>
              {(Object.keys(TYPE_META) as FindingType[]).map((k) => <MenuItem key={k} value={k}>{TYPE_META[k].emoji} {TYPE_META[k].label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value)} sx={{ width: 110 }}
              SelectProps={{ MenuProps: { sx: { zIndex: HUD_MENU_Z } } }}>
              {['low', 'normal', 'high'].map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </TextField>
          </Stack>

          <TextField fullWidth multiline minRows={2} size="small" placeholder="What did you find? (⌘V to paste a screenshot here)" value={note} onChange={(e) => setNote(e.target.value)} onPaste={onNotePaste} sx={{ mb: 1 }} />

          <Button fullWidth size="small" variant="contained" disabled={s.busy || !hasStaged} onClick={saveFinding}>
            {s.busy ? 'Saving…' : `Save finding${shots.length ? ` (${shots.length} shot${shots.length > 1 ? 's' : ''})` : ''}`}
          </Button>
          {s.error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{s.error}</Typography>}

          {s.findings.length > 0 && (
            <Box sx={{ mt: 1.5, maxHeight: 160, overflowY: 'auto' }}>
              {s.findings.slice().reverse().map((f) => (
                <Box key={f.seq} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                  <Chip size="small" color={TYPE_META[f.finding_type]?.color || 'default'} label={`#${f.seq}`} sx={{ height: 18 }} />
                  <Typography variant="caption" sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.title || f.note || '(no note)'}</Typography>
                  {(f.shot_count ?? (f.has_screenshot ? 1 : 0)) > 0 && (
                    <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}>
                      <PhotoCameraIcon sx={{ fontSize: 14, opacity: 0.6 }} />{(f.shot_count ?? 1) > 1 ? `×${f.shot_count}` : ''}
                    </Typography>
                  )}
                  <IconButton size="small" onClick={() => debugSessionController.deleteFinding(f.seq)}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Button fullWidth size="small" variant="outlined" startIcon={<DownloadIcon />} sx={{ mt: 1.5 }} disabled={s.busy} onClick={finishAndExport}>
            Finish &amp; export ZIP
          </Button>
        </Box>
      )}
    </Paper>
  );

  return (
    <>
      {createPortal(body, document.body)}
      {lightbox && <ImageLightbox url={lightbox} onClose={() => setLightbox(null)} />}
      {inspectorMode && <DebugInspector mode={inspectorMode} onComplete={onInspectorDone} onCancel={() => setInspectorMode(null)} />}
    </>
  );
};

export default DebugSessionOverlay;
